import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth/auth-context';
import { getClient, getTaliApiBaseUrl, getTaliRestHeaders } from '@/lib/graphql/client';
import {
  GET_ABANDONED_CARTS,
  GET_ABANDONED_CART_SETTINGS,
  GET_AFFILIATES,
  GET_AFFILIATE_SETTINGS,
} from '@/lib/graphql/operations/marketing';
import {
  GET_AUTOMATIONS,
  GET_AUTOMATION_SECRETS,
} from '@/lib/graphql/operations/automation';
import {
  GET_MERCHANT_REVIEWS,
  GET_REVIEW_REQUESTS,
} from '@/lib/graphql/operations/reviews';
import {
  GET_TESTIMONIALS,
  GET_TESTIMONIAL_SETTINGS,
} from '@/lib/graphql/operations/testimonials';
import { GET_ALL_QUESTIONS } from '@/lib/graphql/operations/qa';
import { GET_APP_INSTALLATIONS } from '@/lib/graphql/operations/apps';
import { GET_BACK_IN_STOCK_REPORT } from '@/lib/graphql/operations/customers';
import { GET_GIFT_CARDS } from '@/lib/graphql/operations/gift-cards';
import { GET_LOYALTY_LIABILITY_SUMMARY } from '@/lib/graphql/operations/reports';

export type CutObligation =
  | 'affiliates'
  | 'automations'
  | 'abandonedCarts'
  | 'reviews'
  | 'reviewRequests'
  | 'questions'
  | 'backInStock'
  | 'testimonials'
  | 'giftCards'
  | 'loyaltyLiability'
  | 'appInstallations'
  | 'dataMigrationJobs';

export type CutObligationState = Record<CutObligation, boolean>;

const EMPTY_STATE: CutObligationState = {
  affiliates: false,
  automations: false,
  abandonedCarts: false,
  reviews: false,
  reviewRequests: false,
  questions: false,
  backInStock: false,
  testimonials: false,
  giftCards: false,
  loyaltyLiability: false,
  appInstallations: false,
  dataMigrationJobs: false,
};

const API_BASE = getTaliApiBaseUrl();

/**
 * Determines whether a CUT capability has tenant-scoped records that still
 * need read, reconciliation, cancellation, disable, or cleanup access.
 * Unknown and failed reads deliberately hide the link; direct routes retain
 * their independent authorization and read/cleanup contracts.
 */
export function useCutObligations(requested: ReadonlySet<CutObligation>): CutObligationState {
  const { activeChannel } = useAuth();
  const channelId = activeChannel?.id;
  const requestedKey = [...requested].sort().join(',');
  const [resolved, setResolved] = useState<{
    channelId?: string;
    values: CutObligationState;
  }>({ channelId: undefined, values: EMPTY_STATE });

  useEffect(() => {
    setResolved({ channelId, values: EMPTY_STATE });
    if (!channelId || requestedKey.length === 0) return;

    let cancelled = false;
    const client = getClient();
    const requestedKeys = new Set(requestedKey.split(',') as CutObligation[]);
    const queries: Array<Promise<[CutObligation, boolean]>> = [];
    const addQuery = <T,>(
      key: CutObligation,
      query: Parameters<typeof client.query>[0],
      variables: Record<string, unknown>,
      hasObligation: (data: T) => boolean,
    ) => {
      if (!requestedKeys.has(key)) return;
      queries.push(
        client.query<T>(query, variables, { requestPolicy: 'network-only' })
          .toPromise()
          .then((result): [CutObligation, boolean] => [
            key,
            !result.error && Boolean(result.data) && hasObligation(result.data as T),
          ])
          .catch((): [CutObligation, boolean] => [key, false]),
      );
    };

    addQuery<{ affiliates: { totalItems: number } }>(
      'affiliates', GET_AFFILIATES, { options: { take: 1 } },
      data => data.affiliates.totalItems > 0,
    );
    addQuery<{ affiliateSettings: { isEnabled: boolean } }>(
      'affiliates', GET_AFFILIATE_SETTINGS, {},
      data => data.affiliateSettings.isEnabled,
    );
    addQuery<{ automations: { totalItems: number } }>(
      'automations', GET_AUTOMATIONS, { options: { take: 1 } },
      data => data.automations.totalItems > 0,
    );
    addQuery<{ automationSecrets: unknown[] }>(
      'automations', GET_AUTOMATION_SECRETS, {},
      data => data.automationSecrets.length > 0,
    );
    addQuery<{ orders: { totalItems: number } }>(
      'abandonedCarts',
      GET_ABANDONED_CARTS,
      { options: { take: 1, filter: { customFields: { abandonedAt: { isNull: false } } } } },
      data => data.orders.totalItems > 0,
    );
    addQuery<{ abandonedCartSettings: { enabled: boolean } }>(
      'abandonedCarts', GET_ABANDONED_CART_SETTINGS, {},
      data => data.abandonedCartSettings.enabled,
    );
    addQuery<{ merchantReviews: { totalItems: number } }>(
      'reviews', GET_MERCHANT_REVIEWS, { options: { take: 1 } },
      data => data.merchantReviews.totalItems > 0,
    );
    addQuery<{ reviewRequests: { totalItems: number } }>(
      'reviewRequests', GET_REVIEW_REQUESTS, { options: { take: 1 } },
      data => data.reviewRequests.totalItems > 0,
    );
    addQuery<{ allQuestions: { totalItems: number } }>(
      'questions', GET_ALL_QUESTIONS, { options: { take: 1 } },
      data => data.allQuestions.totalItems > 0,
    );
    addQuery<{ backInStockReport: { totalItems: number } }>(
      'backInStock', GET_BACK_IN_STOCK_REPORT, { options: { take: 1 } },
      data => data.backInStockReport.totalItems > 0,
    );
    addQuery<{ testimonials: { totalItems: number } }>(
      'testimonials', GET_TESTIMONIALS, { options: { take: 1 } },
      data => data.testimonials.totalItems > 0,
    );
    addQuery<{ testimonialSettings: { enabled: boolean; acceptLinkSubmissions: boolean } }>(
      'testimonials', GET_TESTIMONIAL_SETTINGS, {},
      data => data.testimonialSettings.enabled || data.testimonialSettings.acceptLinkSubmissions,
    );
    addQuery<{ giftCards: { totalItems: number } }>(
      'giftCards', GET_GIFT_CARDS, { options: { take: 1 } },
      data => data.giftCards.totalItems > 0,
    );
    addQuery<{ loyaltyLiabilitySummary: {
      totalPointsOutstanding: number;
      totalCashbackOutstanding: number;
      combinedLiabilityMinorUnits: number;
    } }>(
      'loyaltyLiability', GET_LOYALTY_LIABILITY_SUMMARY, {},
      data => data.loyaltyLiabilitySummary.totalPointsOutstanding > 0
        || data.loyaltyLiabilitySummary.totalCashbackOutstanding > 0
        || data.loyaltyLiabilitySummary.combinedLiabilityMinorUnits > 0,
    );
    addQuery<{ appInstallations: unknown[] }>(
      'appInstallations', GET_APP_INSTALLATIONS, {},
      data => data.appInstallations.length > 0,
    );

    if (requestedKeys.has('dataMigrationJobs')) {
      queries.push(
        fetch(`${API_BASE}/data-migration/jobs`, { headers: getTaliRestHeaders(), credentials: 'include' })
          .then(async response => response.ok ? response.json() : null)
          .then((data): [CutObligation, boolean] => [
            'dataMigrationJobs',
            (data?.items?.length ?? 0) > 0,
          ])
          .catch((): [CutObligation, boolean] => ['dataMigrationJobs', false]),
      );
    }

    void Promise.all(queries).then(entries => {
      if (cancelled) return;
      const values = { ...EMPTY_STATE };
      for (const [key, value] of entries) values[key] = values[key] || value;
      setResolved({ channelId, values });
    });

    return () => {
      cancelled = true;
    };
  }, [channelId, requestedKey]);

  return resolved.channelId === channelId ? resolved.values : EMPTY_STATE;
}
